import { CommonModule, DatePipe } from '@angular/common';
import { Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { BackofficeSessionService } from '../core/backoffice-session.service';
import {
  BackofficeTournamentItem,
  BackofficeTournamentUpsertPayload
} from '../core/backoffice-tournaments-api.types';
import { BackofficeTournamentsApiService } from '../core/backoffice-tournaments-api.service';
import { I18nService } from '../core/i18n.service';

@Component({
  selector: 'app-backoffice-tournaments-page',
  standalone: true,
  imports: [CommonModule, RouterLink, DatePipe, FormsModule],
  template: `
    <section class="page">
      <h1>{{ t('bo.tournaments.title') }}</h1>
      <p class="subtitle">{{ t('bo.tournaments.subtitle') }}</p>

      @if (canGenerateForRole()) {
        <section class="editor">
          <div class="editor-header">
            <div>
              <h2>{{ editingTournamentId() ? t('bo.tournaments.editTitle') : t('bo.tournaments.createTitle') }}</h2>
              <p class="state">
                {{ editingTournamentId() ? t('bo.tournaments.editSubtitle') : t('bo.tournaments.createSubtitle') }}
              </p>
            </div>
            @if (editingTournamentId()) {
              <button type="button" class="button-secondary" [disabled]="saving()" (click)="resetForm()">
                {{ t('bo.tournaments.cancelEdit') }}
              </button>
            }
          </div>

          <div class="grid">
            <label>
              {{ t('bo.tournaments.form.name') }}
              <input
                type="text"
                [ngModel]="form().name"
                (ngModelChange)="updateName($event)"
                [disabled]="saving()"
              />
            </label>

            <label>
              {{ t('bo.tournaments.form.slug') }}
              <input
                type="text"
                [ngModel]="form().slug"
                (ngModelChange)="updateSlug($event)"
                [disabled]="saving()"
              />
            </label>

            <label>
              {{ t('bo.tournaments.form.game') }}
              <input
                type="text"
                [ngModel]="form().game"
                (ngModelChange)="patchForm({ game: normalizeString($event) })"
                [disabled]="saving()"
              />
            </label>

            <label>
              {{ t('bo.tournaments.form.tournamentType') }}
              <select
                [ngModel]="form().tournament_type"
                (ngModelChange)="patchForm({ tournament_type: $event })"
                [disabled]="saving()"
              >
                <option value="equipe">{{ t('bo.tournaments.form.teamTournament') }}</option>
                <option value="individuel">{{ t('bo.tournaments.form.individualTournament') }}</option>
              </select>
            </label>

            <label>
              {{ t('bo.tournaments.form.formatLabel') }}
              <input
                type="text"
                [ngModel]="form().format_label"
                (ngModelChange)="patchForm({ format_label: normalizeString($event) })"
                [disabled]="saving()"
              />
            </label>

            <label>
              {{ t('bo.tournaments.form.region') }}
              <input
                type="text"
                [ngModel]="form().region"
                (ngModelChange)="patchForm({ region: normalizeString($event) })"
                [disabled]="saving()"
              />
            </label>

            <label>
              {{ t('bo.tournaments.form.startsAt') }}
              <input
                type="datetime-local"
                [ngModel]="form().starts_at"
                (ngModelChange)="patchForm({ starts_at: normalizeString($event) })"
                [disabled]="saving()"
              />
            </label>

            <label>
              {{ t('bo.tournaments.form.registrationClosesAt') }}
              <input
                type="datetime-local"
                [ngModel]="form().registration_closes_at"
                (ngModelChange)="patchForm({ registration_closes_at: normalizeString($event) })"
                [disabled]="saving()"
              />
            </label>

            <label>
              {{ t('bo.tournaments.form.checkInOpensAt') }}
              <input
                type="datetime-local"
                [ngModel]="form().check_in_opens_at"
                (ngModelChange)="patchForm({ check_in_opens_at: normalizeString($event) })"
                [disabled]="saving()"
              />
            </label>

            <label>
              {{ t('bo.tournaments.form.checkInClosesAt') }}
              <input
                type="datetime-local"
                [ngModel]="form().check_in_closes_at"
                (ngModelChange)="patchForm({ check_in_closes_at: normalizeString($event) })"
                [disabled]="saving()"
              />
            </label>

            <label>
              {{ t('bo.tournaments.form.bracketCapacity') }}
              <select
                [ngModel]="form().bracket_capacity"
                (ngModelChange)="updateBracketCapacity($event)"
                [disabled]="saving()"
              >
                @for (capacity of bracketCapacities; track capacity) {
                  <option [ngValue]="capacity">{{ capacity }}</option>
                }
              </select>
            </label>

            <label>
              {{ t('bo.tournaments.form.bracketType') }}
              <select
                [ngModel]="form().bracket_type"
                (ngModelChange)="patchForm({ bracket_type: $event })"
                [disabled]="saving()"
              >
                @for (bracketType of supportedBracketTypesForForm(); track bracketType) {
                  <option [value]="bracketType">{{ bracketTypeLabel(bracketType) }}</option>
                }
              </select>
            </label>
          </div>

          <label class="toggle">
            <input
              type="checkbox"
              [ngModel]="form().crossplay"
              (ngModelChange)="patchForm({ crossplay: !!$event })"
              [disabled]="saving()"
            />
            <span>{{ t('bo.tournaments.form.crossplay') }}</span>
          </label>

          <div class="grid grid--full">
            <label>
              {{ t('bo.tournaments.form.description') }}
              <textarea
                [ngModel]="form().description"
                (ngModelChange)="patchForm({ description: normalizeOptionalString($event) })"
                [disabled]="saving()"
              ></textarea>
            </label>

            <label>
              {{ t('bo.tournaments.form.rules') }}
              <textarea
                [ngModel]="form().rules"
                (ngModelChange)="patchForm({ rules: normalizeOptionalString($event) })"
                [disabled]="saving()"
              ></textarea>
            </label>

            <label>
              {{ t('bo.tournaments.form.prizePool') }}
              <input
                type="text"
                [ngModel]="form().prize_pool"
                (ngModelChange)="patchForm({ prize_pool: normalizeOptionalString($event) })"
                [disabled]="saving()"
              />
            </label>

            <label>
              {{ t('bo.tournaments.form.bannerUrl') }}
              <input
                type="text"
                [ngModel]="form().banner_url"
                (ngModelChange)="patchForm({ banner_url: normalizeOptionalString($event) })"
                [disabled]="saving()"
              />
            </label>
          </div>

          <div class="editor-actions">
            <button type="button" [disabled]="saving()" (click)="saveDraft()">
              {{ saving() ? t('bo.tournaments.saving') : t('bo.tournaments.saveDraft') }}
            </button>
            <button type="button" [disabled]="saving()" (click)="publishNow()">
              {{ saving() ? t('bo.tournaments.saving') : t('bo.tournaments.publishNow') }}
            </button>
          </div>

          @if (formSuccessMessage()) {
            <p class="state state--success">{{ formSuccessMessage() }}</p>
          }
          @if (formErrorMessage()) {
            <p class="state state--error">{{ formErrorMessage() }}</p>
          }
        </section>
      }

      @if (loading()) {
        <p class="state">{{ t('bo.tournaments.loading') }}</p>
      } @else if (errorMessage()) {
        <p class="state state--error">{{ errorMessage() }}</p>
      } @else {
        @if (activeTournaments().length === 0 && archivedTournaments().length === 0) {
          <article class="empty">
            <p><strong>{{ t('bo.tournaments.emptyTitle') }}</strong></p>
            <p class="state">{{ t('bo.tournaments.emptySubtitle') }}</p>
          </article>
        } @else {
          @if (activeTournaments().length === 0) {
            <article class="empty">
              <p><strong>{{ t('bo.tournaments.noActiveTitle') }}</strong></p>
              <p class="state">{{ t('bo.tournaments.noActiveSubtitle') }}</p>
            </article>
          } @else {
            <ul class="list">
              @for (tournament of activeTournaments(); track tournament.id) {
                <li class="item">
                  <p class="title">{{ tournament.name }} <span class="slug">{{ tournament.slug }}</span></p>
                  <p>{{ t('bo.tournaments.status', { value: tournament.status }) }}</p>
                  <p>{{ t('bo.tournaments.lifecycleStatus', { value: lifecycleStatusLabel(tournament.lifecycle_status) }) }}</p>
                  <p>{{ t('bo.tournaments.bracketCapacityValue', { value: tournament.bracket_capacity }) }}</p>
                  <p>{{ t('bo.tournaments.bracketTypeValue', { value: bracketTypeLabel(tournament.bracket_type) }) }}</p>
                  <p>{{ t('bo.tournaments.bracketStateValue', { value: bracketStateLabel(tournament.bracket_status) }) }}</p>
                  <p>
                    {{
                      t('bo.tournaments.registrationClose', {
                        value: tournament.registration_closes_at ? ((tournament.registration_closes_at | date: 'short') ?? '-') : '-'
                      })
                    }}
                  </p>
                  <p>{{ t('bo.tournaments.eligibleTeams', { value: tournament.bracket_generation?.eligible_team_count ?? 0 }) }}</p>
                  <p>{{ t('bo.tournaments.estimatedRounds', { value: tournament.bracket_generation?.estimated_rounds ?? '-' }) }}</p>

                  @if (tournament.status === 'draft' && canGenerateForRole()) {
                    <div class="draft-actions">
                      <button type="button" class="button-secondary" [disabled]="saving()" (click)="openDraftEditor(tournament)">
                        {{ t('bo.tournaments.editDraft') }}
                      </button>
                      <button type="button" [disabled]="saving()" (click)="publishExistingTournament(tournament)">
                        {{ savingTournamentId() === tournament.id ? t('bo.tournaments.publishing') : t('bo.tournaments.publishNow') }}
                      </button>
                    </div>
                  }

                  <p>
                    <a [routerLink]="['/backoffice/tournaments', tournament.id, 'registrations']">{{ t('bo.tournaments.viewRegistrations') }}</a>
                  </p>
                  @if (tournament.has_bracket) {
                    <p>
                      <a [routerLink]="['/backoffice/tournaments', tournament.id, 'bracket']">{{ t('bo.tournaments.previewBracket') }}</a>
                    </p>
                  }

                  @if (canGenerateForRole()) {
                    <div class="archive-actions">
                      <button
                        type="button"
                        class="button-secondary"
                        [disabled]="archivingTournamentId() === tournament.id"
                        (click)="archiveTournament(tournament)"
                      >
                        {{
                          archivingTournamentId() === tournament.id
                            ? t('bo.tournaments.archiving')
                            : t('bo.tournaments.archive')
                          }}
                      </button>
                      @if (canMarkFinished(tournament)) {
                        <button
                          type="button"
                          class="button-secondary"
                          [disabled]="finishingTournamentId() === tournament.id"
                          (click)="finishTournament(tournament)"
                        >
                          {{
                            finishingTournamentId() === tournament.id
                              ? t('bo.tournaments.finishing')
                              : t('bo.tournaments.finish')
                          }}
                        </button>
                      } @else if (canReopenLifecycle(tournament)) {
                        <button
                          type="button"
                          class="button-secondary"
                          [disabled]="finishingTournamentId() === tournament.id"
                          (click)="reopenTournament(tournament)"
                        >
                          {{
                            finishingTournamentId() === tournament.id
                              ? t('bo.tournaments.reopening')
                              : t('bo.tournaments.reopen')
                          }}
                        </button>
                      }
                    </div>
                  }

                  @if (isPublishedBracket(tournament)) {
                    <p class="state">{{ t('bo.tournaments.alreadyGenerated') }}</p>
                  } @else if (!canGenerateForRole()) {
                    <p class="state">{{ t('bo.tournaments.readOnlyRole') }}</p>
                  } @else if (!isDraftBracket(tournament) && !(tournament.bracket_generation?.can_generate_draft ?? false)) {
                    <p class="state">{{ t('bo.tournaments.notEligible') }}</p>
                  } @else {
                    @if (setupTournamentId() !== tournament.id) {
                      <button
                        type="button"
                        [disabled]="generatingTournamentId() === tournament.id || publishingBracketTournamentId() === tournament.id"
                        (click)="openGenerationSetup(tournament)"
                      >
                        {{ isDraftBracket(tournament) ? t('bo.tournaments.manageDraftBracket') : t('bo.tournaments.openSetup') }}
                      </button>
                    } @else {
                      <section class="setup">
                        <p class="setup-title">
                          {{ isDraftBracket(tournament) ? t('bo.tournaments.setupTitleDraft') : t('bo.tournaments.setupTitle') }}
                        </p>
                        <label>
                          {{ t('bo.tournaments.bracketType') }}
                          <select
                            [value]="selectedBracketTypeFor(tournament)"
                            [disabled]="generatingTournamentId() === tournament.id || publishingBracketTournamentId() === tournament.id || generationOptionsFor(tournament).length <= 1"
                            (change)="updateSelectedBracketType(tournament.id, $any($event.target).value)"
                          >
                            @for (bracketType of generationOptionsFor(tournament); track bracketType) {
                              <option [value]="bracketType">
                                {{ bracketTypeLabel(bracketType) }}
                              </option>
                            }
                          </select>
                        </label>

                        <label>
                          {{ t('bo.tournaments.drawMode') }}
                          <select
                            [value]="selectedDrawModeFor(tournament)"
                            [disabled]="generatingTournamentId() === tournament.id || publishingBracketTournamentId() === tournament.id || drawModeOptionsFor(tournament).length <= 1"
                            (change)="updateSelectedDrawMode(tournament.id, $any($event.target).value)"
                          >
                            @for (drawMode of drawModeOptionsFor(tournament); track drawMode) {
                              <option [value]="drawMode">
                                {{ drawModeLabel(drawMode) }}
                              </option>
                            }
                          </select>
                        </label>

                        <ul class="checks">
                          <li [class.ok]="tournament.bracket_generation?.checks?.tournament_published">• {{ t('bo.tournaments.rulePublished') }}</li>
                          <li [class.ok]="tournament.bracket_generation?.checks?.minimum_teams">• {{ t('bo.tournaments.ruleMinimumTeams') }}</li>
                          <li [class.ok]="tournament.bracket_generation?.checks?.capacity_compatible">• {{ t('bo.tournaments.ruleCapacityCompatible') }}</li>
                          <li [class.ok]="tournament.bracket_generation?.checks?.no_published_bracket">• {{ t('bo.tournaments.ruleNoPublishedBracket') }}</li>
                          @if (selectedDrawModeFor(tournament) === 'seeded') {
                            <li [class.ok]="tournament.bracket_generation?.checks?.seeding_ready">• {{ t('bo.tournaments.ruleSeedingReady') }}</li>
                          } @else if (selectedDrawModeFor(tournament) === 'manual') {
                            <li [class.ok]="tournament.bracket_generation?.checks?.manual_ready">• {{ t('bo.tournaments.ruleManualReady') }}</li>
                          } @else if (selectedDrawModeFor(tournament) === 'wheel') {
                            <li [class.ok]="tournament.bracket_generation?.checks?.wheel_ready">• {{ t('bo.tournaments.ruleWheelReady') }}</li>
                          }
                          @if (isDraftBracket(tournament)) {
                            <li [class.ok]="tournament.bracket_generation?.checks?.registration_closed">• {{ t('bo.tournaments.ruleRegistrationClosed') }}</li>
                            <li [class.ok]="tournament.bracket_generation?.checks?.draft_matches_current_teams">• {{ t('bo.tournaments.ruleDraftMatchesCurrentTeams') }}</li>
                          }
                        </ul>

                        <div class="setup-actions">
                          <button
                            type="button"
                            [disabled]="generatingTournamentId() === tournament.id || !canRunGenerateAction(tournament)"
                            (click)="generate(tournament)"
                          >
                            {{
                              generatingTournamentId() === tournament.id
                                ? t('bo.tournaments.generating')
                                : generateActionLabel(tournament)
                            }}
                          </button>
                          @if (isDraftBracket(tournament)) {
                            <button
                              type="button"
                              [class.button-disabled]="!canPublishDraft(tournament)"
                              [disabled]="publishingBracketTournamentId() === tournament.id || !canPublishDraft(tournament)"
                              (click)="publishDraftBracket(tournament)"
                            >
                              {{
                                publishingBracketTournamentId() === tournament.id
                                  ? t('bo.tournaments.publishingBracket')
                                  : t('bo.tournaments.publishBracket')
                              }}
                            </button>
                          }
                          <button type="button" class="button-secondary" [disabled]="generatingTournamentId() === tournament.id" (click)="closeGenerationSetup()">
                            {{ t('bo.tournaments.cancelSetup') }}
                          </button>
                        </div>

                        @if (isDraftBracket(tournament) && !canPublishDraft(tournament)) {
                          <p class="state">{{ publishBlockedReason(tournament) }}</p>
                        }
                      </section>
                    }
                  }

                  @if (lastSuccessTournamentId() === tournament.id) {
                    <p class="state state--success">{{ lastSuccessMessage() }}</p>
                  }
                  @if (lastErrorTournamentId() === tournament.id && lastErrorMessage()) {
                    <p class="state state--error">{{ lastErrorMessage() }}</p>
                  }
                </li>
              }
            </ul>
          }

          @if (archivedTournaments().length > 0) {
            <section class="archived-section">
              <div class="section-header">
                <div>
                  <p class="setup-title">{{ t('bo.tournaments.archivedTitle', { count: archivedTournaments().length }) }}</p>
                  <p class="section-subtitle">{{ t('bo.tournaments.archivedSubtitle') }}</p>
                </div>
                <button type="button" class="button-secondary archive-toggle" (click)="toggleArchivedSection()">
                  {{ showArchived() ? t('bo.tournaments.hideArchived') : t('bo.tournaments.showArchived') }}
                </button>
              </div>

              @if (showArchived()) {
                <ul class="list">
                  @for (tournament of archivedTournaments(); track tournament.id) {
                    <li class="item item--archived">
                      <p class="title">{{ tournament.name }} <span class="slug">{{ tournament.slug }}</span></p>
                      <p>{{ t('bo.tournaments.status', { value: tournament.status }) }}</p>
                      <p>{{ t('bo.tournaments.lifecycleStatus', { value: lifecycleStatusLabel(tournament.lifecycle_status) }) }}</p>
                      <p>{{ t('bo.tournaments.bracketStateValue', { value: bracketStateLabel(tournament.bracket_status) }) }}</p>
                      <p>
                        {{
                          t('bo.tournaments.archivedAt', {
                            value: tournament.archived_at ? ((tournament.archived_at | date: 'short') ?? '-') : '-'
                          })
                        }}
                      </p>
                      @if (tournament.finished_at) {
                        <p>
                          {{
                            t('bo.tournaments.finishedAt', {
                              value: (tournament.finished_at | date: 'short') ?? '-'
                            })
                          }}
                        </p>
                      }
                      <p>
                        <a [routerLink]="['/backoffice/tournaments', tournament.id, 'registrations']">{{ t('bo.tournaments.viewRegistrations') }}</a>
                      </p>
                      @if (tournament.has_bracket) {
                        <p>
                          <a [routerLink]="['/backoffice/tournaments', tournament.id, 'bracket']">{{ t('bo.tournaments.previewBracket') }}</a>
                        </p>
                      }
                      @if (canGenerateForRole()) {
                        <div class="archive-actions">
                          <button
                            type="button"
                            class="button-secondary"
                            [disabled]="archivingTournamentId() === tournament.id"
                            (click)="unarchiveTournament(tournament)"
                          >
                            {{
                              archivingTournamentId() === tournament.id
                                ? t('bo.tournaments.unarchiving')
                                : t('bo.tournaments.unarchive')
                            }}
                          </button>
                          @if (canMarkFinished(tournament)) {
                            <button
                              type="button"
                              class="button-secondary"
                              [disabled]="finishingTournamentId() === tournament.id"
                              (click)="finishTournament(tournament)"
                            >
                              {{
                                finishingTournamentId() === tournament.id
                                  ? t('bo.tournaments.finishing')
                                  : t('bo.tournaments.finish')
                              }}
                            </button>
                          } @else if (canReopenLifecycle(tournament)) {
                            <button
                              type="button"
                              class="button-secondary"
                              [disabled]="finishingTournamentId() === tournament.id"
                              (click)="reopenTournament(tournament)"
                            >
                              {{
                                finishingTournamentId() === tournament.id
                                  ? t('bo.tournaments.reopening')
                                  : t('bo.tournaments.reopen')
                              }}
                            </button>
                          }
                        </div>
                      }
                      @if (lastSuccessTournamentId() === tournament.id) {
                        <p class="state state--success">{{ lastSuccessMessage() }}</p>
                      }
                      @if (lastErrorTournamentId() === tournament.id && lastErrorMessage()) {
                        <p class="state state--error">{{ lastErrorMessage() }}</p>
                      }
                    </li>
                  }
                </ul>
              }
            </section>
          }
        }
      }

      <p class="footer-link">
        <a routerLink="/backoffice/score-reports">{{ t('bo.tournaments.toReports') }}</a>
      </p>
    </section>
  `,
  styles: `
    .page { padding: 1.2rem; max-width: 960px; margin: 0 auto; }
    h1 { margin: 0 0 0.35rem; font-size: 1.45rem; color: var(--text-strong); }
    h2 { margin: 0 0 0.25rem; color: var(--text-strong); font-size: 1.12rem; }
    .subtitle { margin: 0 0 0.9rem; color: var(--text-muted); }
    .editor { margin: 0 0 1rem; border: 1px solid var(--line-soft); border-radius: 10px; padding: 0.85rem; background: linear-gradient(180deg, rgba(23, 36, 58, 0.94) 0%, rgba(18, 29, 47, 0.94) 100%); box-shadow: var(--shadow-soft); }
    .editor-header { display: flex; justify-content: space-between; gap: 0.75rem; align-items: flex-start; margin-bottom: 0.75rem; }
    .grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 0.7rem; margin-bottom: 0.7rem; }
    .grid--full { grid-template-columns: 1fr; }
    label { display: grid; gap: 0.28rem; color: #dbe9ff; font-size: 0.9rem; }
    input, select, textarea { padding: 0.55rem 0.6rem; border: 1px solid var(--line-soft); border-radius: 8px; background: rgba(13, 21, 34, 0.9); color: var(--text-strong); font: inherit; }
    textarea { min-height: 88px; resize: vertical; }
    .toggle { display: flex; align-items: center; gap: 0.5rem; margin: 0 0 0.8rem; }
    .toggle input { margin: 0; width: 16px; height: 16px; }
    .editor-actions, .draft-actions, .setup-actions, .archive-actions { display: flex; gap: 0.45rem; flex-wrap: wrap; }
    .draft-actions { margin: 0.45rem 0 0.2rem; }
    .archive-actions { margin-top: 0.45rem; }
    .list { list-style: none; margin: 0; padding: 0; display: grid; gap: 0.65rem; }
    .item { border: 1px solid var(--line-soft); border-radius: 10px; padding: 0.75rem; background: linear-gradient(180deg, rgba(23, 36, 58, 0.94) 0%, rgba(18, 29, 47, 0.94) 100%); box-shadow: var(--shadow-soft); }
    .item--archived { opacity: 0.92; }
    .title { margin: 0 0 0.35rem; color: var(--text-strong); font-weight: 700; }
    .slug { margin-left: 0.4rem; color: var(--text-muted); font-weight: 500; font-size: 0.85rem; }
    .item p { margin: 0.2rem 0; color: var(--text-muted); }
    button { margin-top: 0.45rem; border: 1px solid rgba(138, 184, 255, 0.6); border-radius: 8px; padding: 0.52rem 0.75rem; font-weight: 700; background: linear-gradient(180deg, var(--accent) 0%, var(--accent-strong) 100%); color: #091325; }
    button:disabled, .button-disabled { background: rgba(47, 62, 88, 0.42); color: #b7c5dc; border-color: rgba(138, 184, 255, 0.2); cursor: not-allowed; box-shadow: none; }
    .setup { margin-top: 0.55rem; border: 1px solid rgba(115, 159, 230, 0.32); border-radius: 8px; background: rgba(12, 19, 31, 0.5); padding: 0.6rem; }
    .setup-title { margin: 0 0 0.4rem; color: var(--text-strong); font-weight: 700; }
    .checks { list-style: none; margin: 0.2rem 0 0.6rem; padding: 0; display: grid; gap: 0.18rem; color: var(--text-muted); font-size: 0.88rem; }
    .checks li.ok { color: var(--ok-text); }
    .button-secondary { background: rgba(47, 62, 88, 0.75); color: var(--text-strong); border: 1px solid var(--line-soft); }
    .archived-section { margin-top: 1rem; border: 1px solid rgba(115, 159, 230, 0.22); border-radius: 10px; padding: 0.75rem; background: rgba(12, 19, 31, 0.4); }
    .section-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 0.75rem; margin-bottom: 0.6rem; }
    .section-subtitle { margin: 0.2rem 0 0; color: var(--text-muted); }
    .archive-toggle { margin-top: 0; }
    .empty { border: 1px solid var(--line-soft); border-radius: 10px; background: linear-gradient(180deg, rgba(23, 36, 58, 0.94) 0%, rgba(18, 29, 47, 0.94) 100%); padding: 0.75rem; box-shadow: var(--shadow-soft); }
    .empty strong { color: var(--text-strong); }
    .state { color: var(--text-muted); }
    .state--error { color: var(--danger); }
    .state--success { color: var(--ok-text); }
    .footer-link { margin-top: 0.9rem; }
    a { color: var(--accent); text-decoration: none; font-weight: 600; }
  `
})
export class BackofficeTournamentsPageComponent {
  protected readonly bracketCapacities = [2, 4, 8, 16, 32];
  protected readonly form = signal<BackofficeTournamentUpsertForm>(defaultFormState());

  private readonly api = inject(BackofficeTournamentsApiService);
  private readonly backofficeSession = inject(BackofficeSessionService);
  private readonly i18n = inject(I18nService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly tournaments = signal<BackofficeTournamentItem[]>([]);
  protected readonly activeTournaments = computed(() => this.tournaments().filter((tournament) => !tournament.archived_at));
  protected readonly archivedTournaments = computed(() => this.tournaments().filter((tournament) => !!tournament.archived_at));
  protected readonly loading = signal(true);
  protected readonly errorMessage = signal<string | null>(null);
  protected readonly editingTournamentId = signal<number | null>(null);
  protected readonly savingTournamentId = signal<number | null>(null);
  protected readonly generatingTournamentId = signal<number | null>(null);
  protected readonly publishingBracketTournamentId = signal<number | null>(null);
  protected readonly archivingTournamentId = signal<number | null>(null);
  protected readonly finishingTournamentId = signal<number | null>(null);
  protected readonly setupTournamentId = signal<number | null>(null);
  protected readonly showArchived = signal(false);
  protected readonly selectedBracketTypes = signal<Record<number, string>>({});
  protected readonly selectedDrawModes = signal<Record<number, 'random' | 'seeded' | 'manual' | 'wheel'>>({});
  protected readonly lastSuccessTournamentId = signal<number | null>(null);
  protected readonly lastSuccessMessage = signal<string | null>(null);
  protected readonly lastErrorTournamentId = signal<number | null>(null);
  protected readonly lastErrorMessage = signal<string | null>(null);
  protected readonly formSuccessMessage = signal<string | null>(null);
  protected readonly formErrorMessage = signal<string | null>(null);

  private readonly roles = computed(() => this.backofficeSession.identity()?.roles ?? []);
  protected readonly saving = computed(() => this.savingTournamentId() !== null);

  constructor() {
    this.load();
  }

  protected canGenerateForRole(): boolean {
    const roles = this.roles();
    return roles.includes('admin') || roles.includes('management') || roles.includes('owner');
  }

  protected openDraftEditor(tournament: BackofficeTournamentItem): void {
    this.editingTournamentId.set(tournament.id);
    this.form.set(formStateFromTournament(tournament));
    this.formSuccessMessage.set(null);
    this.formErrorMessage.set(null);
    this.scrollEditorIntoView();
  }

  protected resetForm(): void {
    this.editingTournamentId.set(null);
    this.form.set(defaultFormState());
    this.formSuccessMessage.set(null);
    this.formErrorMessage.set(null);
  }

  protected patchForm(patch: Partial<BackofficeTournamentUpsertForm>): void {
    this.form.update((current) => ({ ...current, ...patch }));
  }

  protected updateName(value: string): void {
    const normalizedName = this.normalizeString(value);
    this.form.update((current) => {
      const next = { ...current, name: normalizedName };
      if (!current.slugManuallyEdited) {
        next.slug = slugify(normalizedName);
      }
      return next;
    });
  }

  protected updateSlug(value: string): void {
    this.form.update((current) => ({
      ...current,
      slug: slugify(value),
      slugManuallyEdited: true
    }));
  }

  protected updateBracketCapacity(value: number | string): void {
    const capacity = Number(value);
    this.form.update((current) => {
      const supportedTypes = supportedBracketTypesForCapacity(capacity);
      const nextBracketType = supportedTypes.includes(current.bracket_type) ? current.bracket_type : supportedTypes[0];
      return {
        ...current,
        bracket_capacity: capacity,
        bracket_type: nextBracketType
      };
    });
  }

  protected supportedBracketTypesForForm(): string[] {
    return supportedBracketTypesForCapacity(this.form().bracket_capacity);
  }

  protected saveDraft(): void {
    this.persistForm(false);
  }

  protected publishNow(): void {
    this.persistForm(true);
  }

  protected publishExistingTournament(tournament: BackofficeTournamentItem): void {
    this.savingTournamentId.set(tournament.id);
    this.formSuccessMessage.set(null);
    this.formErrorMessage.set(null);
    this.api
      .publishTournament(tournament.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ tournament: updated }) => {
          this.upsertTournament(updated);
          this.resetForm();
          this.formSuccessMessage.set(this.t('bo.tournaments.publishedSuccess'));
          this.formErrorMessage.set(null);
          this.savingTournamentId.set(null);
        },
        error: (error) => {
          this.formErrorMessage.set(this.resolveApiError(error, this.t('bo.tournaments.publishError')));
          this.formSuccessMessage.set(null);
          this.savingTournamentId.set(null);
        }
      });
  }

  protected archiveTournament(tournament: BackofficeTournamentItem): void {
    if (!this.canGenerateForRole()) {
      this.lastErrorTournamentId.set(tournament.id);
      this.lastErrorMessage.set(this.t('bo.tournaments.readOnlyRole'));
      return;
    }

    this.archivingTournamentId.set(tournament.id);
    this.lastSuccessTournamentId.set(null);
    this.lastSuccessMessage.set(null);
    this.lastErrorTournamentId.set(null);
    this.lastErrorMessage.set(null);

    this.api
      .archiveTournament(tournament.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ tournament: updated }) => {
          if (this.editingTournamentId() === updated.id) {
            this.resetForm();
          }
          if (this.setupTournamentId() === updated.id) {
            this.closeGenerationSetup();
          }
          this.upsertTournament(updated);
          this.showArchived.set(true);
          this.lastSuccessTournamentId.set(updated.id);
          this.lastSuccessMessage.set(this.t('bo.tournaments.archiveSuccess'));
          this.archivingTournamentId.set(null);
        },
        error: (error) => {
          this.archivingTournamentId.set(null);
          this.lastErrorTournamentId.set(tournament.id);
          this.lastErrorMessage.set(this.resolveApiError(error, this.t('bo.tournaments.archiveError')));
        }
      });
  }

  protected unarchiveTournament(tournament: BackofficeTournamentItem): void {
    if (!this.canGenerateForRole()) {
      this.lastErrorTournamentId.set(tournament.id);
      this.lastErrorMessage.set(this.t('bo.tournaments.readOnlyRole'));
      return;
    }

    this.archivingTournamentId.set(tournament.id);
    this.lastSuccessTournamentId.set(null);
    this.lastSuccessMessage.set(null);
    this.lastErrorTournamentId.set(null);
    this.lastErrorMessage.set(null);

    this.api
      .unarchiveTournament(tournament.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ tournament: updated }) => {
          this.upsertTournament(updated);
          this.lastSuccessTournamentId.set(updated.id);
          this.lastSuccessMessage.set(this.t('bo.tournaments.unarchiveSuccess'));
          this.archivingTournamentId.set(null);
        },
        error: (error) => {
          this.archivingTournamentId.set(null);
          this.lastErrorTournamentId.set(tournament.id);
          this.lastErrorMessage.set(this.resolveApiError(error, this.t('bo.tournaments.unarchiveError')));
        }
      });
  }

  protected finishTournament(tournament: BackofficeTournamentItem): void {
    if (!this.canGenerateForRole()) {
      this.lastErrorTournamentId.set(tournament.id);
      this.lastErrorMessage.set(this.t('bo.tournaments.readOnlyRole'));
      return;
    }

    this.finishingTournamentId.set(tournament.id);
    this.lastSuccessTournamentId.set(null);
    this.lastSuccessMessage.set(null);
    this.lastErrorTournamentId.set(null);
    this.lastErrorMessage.set(null);

    this.api
      .finishTournament(tournament.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ tournament: updated }) => {
          this.upsertTournament(updated);
          this.lastSuccessTournamentId.set(updated.id);
          this.lastSuccessMessage.set(this.t('bo.tournaments.finishSuccess'));
          this.finishingTournamentId.set(null);
        },
        error: (error) => {
          this.finishingTournamentId.set(null);
          this.lastErrorTournamentId.set(tournament.id);
          this.lastErrorMessage.set(this.resolveApiError(error, this.t('bo.tournaments.finishError')));
        }
      });
  }

  protected reopenTournament(tournament: BackofficeTournamentItem): void {
    if (!this.canGenerateForRole()) {
      this.lastErrorTournamentId.set(tournament.id);
      this.lastErrorMessage.set(this.t('bo.tournaments.readOnlyRole'));
      return;
    }

    this.finishingTournamentId.set(tournament.id);
    this.lastSuccessTournamentId.set(null);
    this.lastSuccessMessage.set(null);
    this.lastErrorTournamentId.set(null);
    this.lastErrorMessage.set(null);

    this.api
      .reopenTournament(tournament.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ tournament: updated }) => {
          this.upsertTournament(updated);
          this.lastSuccessTournamentId.set(updated.id);
          this.lastSuccessMessage.set(this.t('bo.tournaments.reopenSuccess'));
          this.finishingTournamentId.set(null);
        },
        error: (error) => {
          this.finishingTournamentId.set(null);
          this.lastErrorTournamentId.set(tournament.id);
          this.lastErrorMessage.set(this.resolveApiError(error, this.t('bo.tournaments.reopenError')));
        }
      });
  }

  protected openGenerationSetup(tournament: BackofficeTournamentItem): void {
    if (!this.canGenerateForRole()) {
      this.lastErrorTournamentId.set(tournament.id);
      this.lastErrorMessage.set(this.t('bo.tournaments.readOnlyRole'));
      return;
    }
    if (this.isPublishedBracket(tournament)) {
      this.lastErrorTournamentId.set(tournament.id);
      this.lastErrorMessage.set(this.t('bo.tournaments.alreadyGenerated'));
      return;
    }
    if (!this.isDraftBracket(tournament) && !(tournament.bracket_generation?.can_generate_draft ?? false)) {
      this.lastErrorTournamentId.set(tournament.id);
      this.lastErrorMessage.set(this.t('bo.tournaments.notEligible'));
      return;
    }
    this.updateSelectedBracketType(
      tournament.id,
      tournament.bracket_generation?.selected_bracket_type ??
        tournament.bracket_generation?.supported_bracket_types?.[0] ??
        'single_elimination'
    );
    this.updateSelectedDrawMode(
      tournament.id,
      tournament.bracket_generation?.selected_draw_mode ?? 'random'
    );
    this.setupTournamentId.set(tournament.id);
    this.lastErrorTournamentId.set(null);
    this.lastErrorMessage.set(null);
  }

  protected closeGenerationSetup(): void {
    this.setupTournamentId.set(null);
  }

  protected toggleArchivedSection(): void {
    this.showArchived.update((current) => !current);
  }

  protected generate(tournament: BackofficeTournamentItem): void {
    if (!this.canGenerateForRole()) {
      this.lastErrorTournamentId.set(tournament.id);
      this.lastErrorMessage.set(this.t('bo.tournaments.readOnlyRole'));
      return;
    }
    if (this.isPublishedBracket(tournament)) {
      this.lastErrorTournamentId.set(tournament.id);
      this.lastErrorMessage.set(this.t('bo.tournaments.alreadyGenerated'));
      return;
    }
    if (!this.canRunGenerateAction(tournament)) {
      this.lastErrorTournamentId.set(tournament.id);
      this.lastErrorMessage.set(this.t('bo.tournaments.notEligible'));
      return;
    }

    this.generatingTournamentId.set(tournament.id);
    this.lastSuccessTournamentId.set(null);
    this.lastSuccessMessage.set(null);
    this.lastErrorTournamentId.set(null);
    this.lastErrorMessage.set(null);

    this.api
      .generateBracket(tournament.id, this.selectedBracketTypeFor(tournament), this.selectedDrawModeFor(tournament))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.lastSuccessTournamentId.set(tournament.id);
          this.lastSuccessMessage.set(this.t('bo.tournaments.success'));
          this.generatingTournamentId.set(null);
          this.setupTournamentId.set(null);
          this.load();
        },
        error: (error) => {
          this.generatingTournamentId.set(null);
          this.lastErrorTournamentId.set(tournament.id);
          this.lastErrorMessage.set(this.resolveApiError(error, this.t('bo.tournaments.generateError')));
        }
      });
  }

  protected publishDraftBracket(tournament: BackofficeTournamentItem): void {
    if (!this.canGenerateForRole()) {
      this.lastErrorTournamentId.set(tournament.id);
      this.lastErrorMessage.set(this.t('bo.tournaments.readOnlyRole'));
      return;
    }
    if (!this.isDraftBracket(tournament)) {
      this.lastErrorTournamentId.set(tournament.id);
      this.lastErrorMessage.set(this.t('bo.tournaments.noDraftBracket'));
      return;
    }
    if (!this.canPublishDraft(tournament)) {
      this.lastErrorTournamentId.set(tournament.id);
      this.lastErrorMessage.set(this.t('bo.tournaments.publishBracketNotReady'));
      return;
    }

    this.publishingBracketTournamentId.set(tournament.id);
    this.lastSuccessTournamentId.set(null);
    this.lastSuccessMessage.set(null);
    this.lastErrorTournamentId.set(null);
    this.lastErrorMessage.set(null);

    this.api
      .publishBracket(tournament.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.lastSuccessTournamentId.set(tournament.id);
          this.lastSuccessMessage.set(this.t('bo.tournaments.publishBracketSuccess'));
          this.publishingBracketTournamentId.set(null);
          this.setupTournamentId.set(null);
          this.load();
        },
        error: (error) => {
          this.publishingBracketTournamentId.set(null);
          this.lastErrorTournamentId.set(tournament.id);
          this.lastErrorMessage.set(this.resolveApiError(error, this.t('bo.tournaments.publishBracketError')));
        }
      });
  }

  private load(): void {
    this.loading.set(true);
    this.errorMessage.set(null);
    this.api
      .list()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ tournaments }) => {
          this.tournaments.set([...tournaments].sort((a, b) => a.name.localeCompare(b.name)));
          this.loading.set(false);
        },
        error: (error) => {
          const apiError = error?.error?.error;
          this.errorMessage.set(typeof apiError === 'string' ? apiError : this.t('bo.tournaments.loadError'));
          this.loading.set(false);
        }
      });
  }

  protected t(key: string, params?: Record<string, string | number | null | undefined>): string {
    return this.i18n.t(key, params);
  }

  protected bracketTypeLabel(value: string): string {
    return normalizeBracketType(value) === 'double_elimination'
      ? this.t('bo.tournaments.doubleElimination')
      : this.t('bo.tournaments.singleElimination');
  }

  protected bracketStateLabel(value: BackofficeTournamentItem['bracket_status']): string {
    if (value === 'draft') return this.t('bo.tournaments.bracketStateDraft');
    if (value === 'published') return this.t('bo.tournaments.bracketStatePublished');
    return this.t('bo.tournaments.bracketStateNone');
  }

  protected lifecycleStatusLabel(value: BackofficeTournamentItem['lifecycle_status']): string {
    if (value === 'past') return this.t('bo.tournaments.lifecycle.past');
    if (value === 'ongoing') return this.t('bo.tournaments.lifecycle.ongoing');
    return this.t('bo.tournaments.lifecycle.upcoming');
  }

  protected isDraftBracket(tournament: BackofficeTournamentItem): boolean {
    return tournament.bracket_status === 'draft';
  }

  protected isPublishedBracket(tournament: BackofficeTournamentItem): boolean {
    return tournament.bracket_status === 'published';
  }

  protected canMarkFinished(tournament: BackofficeTournamentItem): boolean {
    return tournament.status === 'published' && !tournament.finished_at && tournament.lifecycle_status !== 'past';
  }

  protected canReopenLifecycle(tournament: BackofficeTournamentItem): boolean {
    return !!tournament.finished_at;
  }

  protected generationOptionsFor(tournament: BackofficeTournamentItem): string[] {
    const supported = tournament.bracket_generation?.supported_bracket_types ?? [];
    return supported.length > 0 ? supported : [this.selectedBracketTypeFor(tournament)];
  }

  protected drawModeOptionsFor(tournament: BackofficeTournamentItem): ('random' | 'seeded' | 'manual' | 'wheel')[] {
    const supported = tournament.bracket_generation?.supported_draw_modes ?? [];
    return supported.length > 0 ? supported : [this.selectedDrawModeFor(tournament)];
  }

  protected selectedBracketTypeFor(tournament: BackofficeTournamentItem): string {
    return (
      this.selectedBracketTypes()[tournament.id] ??
      tournament.bracket_generation?.selected_bracket_type ??
      tournament.bracket_generation?.supported_bracket_types?.[0] ??
      'single_elimination'
    );
  }

  protected selectedDrawModeFor(tournament: BackofficeTournamentItem): 'random' | 'seeded' | 'manual' | 'wheel' {
    return (
      this.selectedDrawModes()[tournament.id] ??
      tournament.bracket_generation?.selected_draw_mode ??
      'random'
    );
  }

  protected canRunGenerateAction(tournament: BackofficeTournamentItem): boolean {
    const drawReady = this.drawModeReady(tournament);
    if (this.isDraftBracket(tournament)) {
      return (tournament.bracket_generation?.can_regenerate_draft ?? false) && drawReady;
    }

    return (tournament.bracket_generation?.can_generate_draft ?? false) && drawReady;
  }

  protected canPublishDraft(tournament: BackofficeTournamentItem): boolean {
    return this.isDraftBracket(tournament) && (tournament.bracket_generation?.can_publish ?? false);
  }

  protected publishBlockedReason(tournament: BackofficeTournamentItem): string {
    const checks = tournament.bracket_generation?.checks;
    if (!checks) {
      return this.t('bo.tournaments.publishBracketNotReady');
    }
    if (!checks.registration_closed) {
      return this.t('bo.tournaments.publishBlockedRegistrationOpen');
    }
    if (this.selectedDrawModeFor(tournament) === 'seeded' && !checks.seeding_ready) {
      return this.t('bo.tournaments.publishBlockedSeedsChanged');
    }
    if (this.selectedDrawModeFor(tournament) === 'manual' && !checks.manual_ready) {
      return this.t('bo.tournaments.publishBlockedManualChanged');
    }
    if (this.selectedDrawModeFor(tournament) === 'wheel' && !checks.wheel_ready) {
      return this.t('bo.tournaments.publishBlockedWheelChanged');
    }
    if (!checks.draft_matches_current_teams) {
      return this.t('bo.tournaments.publishBlockedDraftChanged');
    }
    if (!checks.minimum_teams) {
      return this.t('bo.tournaments.publishBlockedMinimumTeams');
    }
    if (!checks.capacity_compatible) {
      return this.t('bo.tournaments.publishBlockedCapacityExceeded');
    }
    return this.t('bo.tournaments.publishBracketNotReady');
  }

  protected generateActionLabel(tournament: BackofficeTournamentItem): string {
    return this.isDraftBracket(tournament)
      ? this.t('bo.tournaments.regenerateDraft')
      : this.t('bo.tournaments.generateDraft');
  }

  protected updateSelectedBracketType(tournamentId: number, bracketType: string): void {
    this.selectedBracketTypes.update((current) => ({ ...current, [tournamentId]: bracketType }));
  }

  protected updateSelectedDrawMode(tournamentId: number, drawMode: string): void {
    const normalized = drawMode === 'seeded' || drawMode === 'manual' || drawMode === 'wheel' ? drawMode : 'random';
    this.selectedDrawModes.update((current) => ({ ...current, [tournamentId]: normalized }));
  }

  protected drawModeLabel(drawMode: string): string {
    if (drawMode === 'seeded') return this.t('bo.tournaments.drawModeSeeded');
    if (drawMode === 'manual') return this.t('bo.tournaments.drawModeManual');
    if (drawMode === 'wheel') return this.t('bo.tournaments.drawModeWheel');
    return this.t('bo.tournaments.drawModeRandom');
  }

  protected drawModeReady(tournament: BackofficeTournamentItem): boolean {
    const selectedDrawMode = this.selectedDrawModeFor(tournament);
    if (selectedDrawMode === 'seeded') {
      return tournament.bracket_generation?.checks?.seeding_ready ?? false;
    }
    if (selectedDrawMode === 'manual') {
      return tournament.bracket_generation?.checks?.manual_ready ?? false;
    }
    if (selectedDrawMode === 'wheel') {
      return tournament.bracket_generation?.checks?.wheel_ready ?? false;
    }
    return true;
  }

  protected normalizeString(value: string | null | undefined): string {
    return (value ?? '').trim();
  }

  protected normalizeOptionalString(value: string | null | undefined): string | null {
    const normalized = this.normalizeString(value);
    return normalized.length > 0 ? normalized : null;
  }

  private scrollEditorIntoView(): void {
    if (typeof document === 'undefined') return;

    requestAnimationFrame(() => {
      const editor = document.querySelector('.editor');
      editor?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  private persistForm(publishAfterSave: boolean): void {
    const editingTournamentId = this.editingTournamentId();
    const payload = this.buildPayload();

    this.savingTournamentId.set(editingTournamentId ?? -1);
    this.formSuccessMessage.set(null);
    this.formErrorMessage.set(null);

    const request$ = editingTournamentId
      ? this.api.updateTournament(editingTournamentId, payload)
      : this.api.createTournament(payload);

    request$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ tournament }) => {
          this.upsertTournament(tournament);
          if (publishAfterSave) {
            this.publishAfterSave(tournament.id);
            return;
          }

          this.editingTournamentId.set(null);
          this.form.set(defaultFormState());
          this.formSuccessMessage.set(
            editingTournamentId ? this.t('bo.tournaments.draftUpdatedSuccess') : this.t('bo.tournaments.draftCreatedSuccess')
          );
          this.formErrorMessage.set(null);
          this.savingTournamentId.set(null);
        },
        error: (error) => {
          this.savingTournamentId.set(null);
          this.formErrorMessage.set(this.resolveApiError(error, this.t('bo.tournaments.saveError')));
          this.formSuccessMessage.set(null);
        }
      });
  }

  private publishAfterSave(tournamentId: number): void {
    this.savingTournamentId.set(tournamentId);
    this.api
      .publishTournament(tournamentId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ tournament }) => {
          this.upsertTournament(tournament);
          this.editingTournamentId.set(null);
          this.form.set(defaultFormState());
          this.formSuccessMessage.set(this.t('bo.tournaments.publishedSuccess'));
          this.formErrorMessage.set(null);
          this.savingTournamentId.set(null);
        },
        error: (error) => {
          this.savingTournamentId.set(null);
          this.formErrorMessage.set(this.resolveApiError(error, this.t('bo.tournaments.publishError')));
          this.formSuccessMessage.set(null);
        }
      });
  }

  private buildPayload(): BackofficeTournamentUpsertPayload {
    const form = this.form();
    return {
      name: form.name,
      slug: form.slug,
      game: form.game,
      tournament_type: form.tournament_type,
      format_label: form.format_label,
      bracket_type: form.bracket_type,
      bracket_capacity: form.bracket_capacity,
      starts_at: toIsoString(form.starts_at),
      registration_closes_at: toIsoString(form.registration_closes_at),
      check_in_opens_at: toIsoString(form.check_in_opens_at),
      check_in_closes_at: toIsoString(form.check_in_closes_at),
      region: form.region,
      crossplay: form.crossplay,
      description: form.description,
      rules: form.rules,
      prize_pool: form.prize_pool,
      banner_url: form.banner_url
    };
  }

  private upsertTournament(tournament: BackofficeTournamentItem): void {
    this.tournaments.update((rows) => {
      const existingIndex = rows.findIndex((row) => row.id === tournament.id);
      if (existingIndex >= 0) {
        const nextRows = [...rows];
        nextRows[existingIndex] = tournament;
        return [...nextRows].sort((a, b) => a.name.localeCompare(b.name));
      }

      return [...rows, tournament].sort((a, b) => a.name.localeCompare(b.name));
    });
  }

  private resolveApiError(error: unknown, fallback: string): string {
    const payload = (error as { error?: unknown })?.error;
    const apiError = (payload as { error?: unknown })?.error;
    const apiErrors = (payload as { errors?: unknown })?.errors;

    if (typeof apiError === 'string' && apiError.trim().length > 0) {
      return apiError;
    }

    if (Array.isArray(apiErrors) && apiErrors.length > 0) {
      return apiErrors.filter((entry) => typeof entry === 'string').join(' | ');
    }

    if (typeof payload === 'string' && payload.trim().length > 0) {
      if (/^\s*<!doctype html|^\s*<html/i.test(payload)) {
        const status = (error as { status?: unknown })?.status;
        return typeof status === 'number' && status > 0 ? `${fallback} (HTTP ${status})` : fallback;
      }
      return payload;
    }

    const message = (error as { message?: unknown })?.message;
    if (typeof message === 'string' && message.trim().length > 0) {
      return message;
    }

    const status = (error as { status?: unknown })?.status;
    return typeof status === 'number' && status > 0 ? `${fallback} (HTTP ${status})` : fallback;
  }
}

type BackofficeTournamentUpsertForm = {
  name: string;
  slug: string;
  slugManuallyEdited: boolean;
  game: string;
  tournament_type: string;
  format_label: string;
  bracket_type: string;
  bracket_capacity: number;
  starts_at: string;
  registration_closes_at: string;
  check_in_opens_at: string;
  check_in_closes_at: string;
  region: string;
  crossplay: boolean;
  description: string | null;
  rules: string | null;
  prize_pool: string | null;
  banner_url: string | null;
};

function defaultFormState(): BackofficeTournamentUpsertForm {
  const now = new Date();
  const startsAt = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
  const registrationClosesAt = new Date(startsAt.getTime() - 48 * 60 * 60 * 1000);
  const checkInOpensAt = new Date(startsAt.getTime() - 24 * 60 * 60 * 1000);
  const checkInClosesAt = new Date(startsAt.getTime() - 60 * 60 * 1000);

  return {
    name: '',
    slug: '',
    slugManuallyEdited: false,
    game: 'Call of Duty',
    tournament_type: 'equipe',
    format_label: '4v4',
    bracket_type: 'single_elimination',
    bracket_capacity: 4,
    starts_at: toDateTimeLocal(startsAt),
    registration_closes_at: toDateTimeLocal(registrationClosesAt),
    check_in_opens_at: toDateTimeLocal(checkInOpensAt),
    check_in_closes_at: toDateTimeLocal(checkInClosesAt),
    region: 'EU',
    crossplay: false,
    description: null,
    rules: null,
    prize_pool: null,
    banner_url: null
  };
}

function formStateFromTournament(tournament: BackofficeTournamentItem): BackofficeTournamentUpsertForm {
  return {
    name: tournament.name,
    slug: tournament.slug,
    slugManuallyEdited: true,
    game: tournament.game,
    tournament_type: tournament.tournament_type,
    format_label: tournament.format_label,
    bracket_type: normalizeBracketType(tournament.bracket_type),
    bracket_capacity: tournament.bracket_capacity,
    starts_at: toDateTimeLocal(tournament.starts_at),
    registration_closes_at: toDateTimeLocal(tournament.registration_closes_at),
    check_in_opens_at: toDateTimeLocal(tournament.check_in_opens_at),
    check_in_closes_at: toDateTimeLocal(tournament.check_in_closes_at ?? tournament.starts_at),
    region: tournament.region,
    crossplay: !!tournament.crossplay,
    description: tournament.description ?? null,
    rules: tournament.rules ?? null,
    prize_pool: tournament.prize_pool ?? null,
    banner_url: tournament.banner_url ?? null
  };
}

function supportedBracketTypesForCapacity(capacity: number): string[] {
  return capacity === 2 ? ['single_elimination'] : ['single_elimination', 'double_elimination'];
}

function normalizeBracketType(value: string): string {
  return value === 'simple_single' ? 'single_elimination' : value;
}

function slugify(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function toDateTimeLocal(value: string | Date): string {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  const hours = `${date.getHours()}`.padStart(2, '0');
  const minutes = `${date.getMinutes()}`.padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

function toIsoString(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toISOString();
}
